import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, map, Observable, of } from 'rxjs';

import {
  DeleteFridaConfigResult,
  FridaConfig,
  FridaConfigApiItem,
  UpsertFridaConfigRequest,
} from './frida-config.types';
import { apiUrl } from '../../../auth/api-url';

@Injectable({
  providedIn: 'root',
})
export class FridaConfigService {
  private readonly endpoint = apiUrl('fridaconfig');

  constructor(private readonly http: HttpClient) {}

  getFridaConfigs(): Observable<FridaConfig[]> {
    return this.http.get<unknown>(this.endpoint).pipe(
      map((response) => this.mapRows(response, (item) => this.normalizeFridaConfig(item))),
    );
  }

  createFridaConfig(payload: UpsertFridaConfigRequest): Observable<void> {
    return this.http.post<void>(this.endpoint, payload);
  }

  updateFridaConfig(name: string, payload: UpsertFridaConfigRequest): Observable<void> {
    return this.http.put<void>(`${this.endpoint}/${encodeURIComponent(name)}`, payload);
  }

  deleteFridaConfig(id: number): Observable<DeleteFridaConfigResult> {
    return this.http.delete<void>(`${this.endpoint}/${id}`).pipe(
      map((): DeleteFridaConfigResult => ({ kind: 'success' })),
      catchError(() => of<DeleteFridaConfigResult>({
        kind: 'error',
        message: 'Unable to delete frida config at this time. Please try again.',
      })),
    );
  }

  private normalizeFridaConfig(item: FridaConfigApiItem): FridaConfig {
    return {
      id: this.toNumber(item.id),
      name: this.toText(item.name),
      value: this.toText(item.value),
    };
  }

  private mapRows<T>(response: unknown, mapper: (item: FridaConfigApiItem) => T): T[] {
    return this.extractRows(response).map(mapper);
  }

  private extractRows(response: unknown): FridaConfigApiItem[] {
    if (Array.isArray(response)) {
      return response as FridaConfigApiItem[];
    }

    if (response && typeof response === 'object') {
      const record = response as Record<string, unknown>;
      const data = record['data'];
      const items = record['items'];

      if (Array.isArray(data)) {
        return data as FridaConfigApiItem[];
      }

      if (Array.isArray(items)) {
        return items as FridaConfigApiItem[];
      }
    }

    return [];
  }

  private toText(value: unknown): string {
    return typeof value === 'string' ? value : '';
  }

  private toNumber(value: unknown): number {
    return typeof value === 'number' && Number.isFinite(value) ? value : 0;
  }
}
